<template>
    <div v-if="!isClosed" class="quickpay-payment-feedback">
        <template v-if="isMobile && showMobileTeaser">
            <CompassTooltip
                ref="teaserTooltip"
                :content="translations.tooltip_cta"
                placement="left"
                :arrow="false"
            >
                <div class="quickpay-payment-feedback__teaser-bubble" @click="openFeedbackPanel">
                    <CompassIcon icon="richeditor-emoji" size="large" />
                </div>
            </CompassTooltip>
        </template>
        <template v-else>
            <div class="quickpay-payment-feedback__header">
                <h3 class="quickpay-payment-feedback__title typographies-paragraph-medium-bold">
                    {{ currentTitle }}
                </h3>
                <CompassIconButton icon="close" @click="close" />
            </div>
            <div v-if="!isSubmitted" class="quickpay-payment-feedback__buttons">
                <button
                    v-for="reaction in reactions"
                    :key="reaction.value"
                    class="quickpay-payment-feedback__emoji-button"
                    :class="{ 'is-active': selectedReaction === reaction.value }"
                    @click="selectReaction(reaction.value)"
                >
                    <span class="quickpay-payment-feedback__emoji">
                        {{ reaction.emoji }}
                    </span>
                    <span class="typographies-paragraph-medium-bold">
                        {{ reaction.label }}
                    </span>
                </button>
            </div>
            <div v-if="showForm" class="quickpay-payment-feedback__form">
                <CompassInput
                    ref="feedbackTextarea"
                    v-model="comment"
                    type="textarea"
                    :placeholder="translations.comment_placeholder"
                    class="quickpay-payment-feedback__textarea"
                />
                <button
                    class="quickpay-payment-feedback__button typographies-paragraph-medium-bold"
                    @click="submitFeedback"
                >
                    {{ translations.send }}
                </button>
            </div>
            <div v-if="isSubmitted" class="quickpay-payment-feedback__thanks">
                <p
                    class="quickpay-payment-feedback__description typographies-paragraph-medium-regular"
                >
                    {{ translations.thanks_description }}
                </p>
                <button
                    class="quickpay-payment-feedback__button typographies-paragraph-medium-bold"
                    @click="close"
                >
                    {{ translations.close }}
                </button>
            </div>
        </template>
    </div>
</template>

<script>
export default {
    name: 'QuickpayPaymentFeedback',
    props: {
        hash: {
            type: String,
            required: true,
        },
        data: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            selectedReaction: null,
            comment: '',
            isSubmitted: false,
            isClosed: false,
            showMobileTeaser: true,
            translations: {
                tooltip_cta: this.$t.__('quickpay.feedback.tooltip_cta'),
                title: this.$t.__('quickpay.feedback.title'),
                thanks_title: this.$t.__('quickpay.feedback.thanks_title'),
                comment_placeholder: this.$t.__('quickpay.feedback.comment_placeholder'),
                send: this.$t.__('common.actions.send'),
                thanks_description: this.$t.__('quickpay.feedback.thanks_description'),
                close: this.$t.__('common.actions.close'),
                complicated: this.$t.__('quickpay.feedback.complicated'),
                good: this.$t.__('quickpay.feedback.good'),
                perfect: this.$t.__('quickpay.feedback.perfect'),
            },
        };
    },
    computed: {
        currentTitle() {
            return this.isSubmitted ? this.translations.thanks_title : this.translations.title;
        },
        showForm() {
            return this.selectedReaction && !this.isSubmitted;
        },
        reactions() {
            return [
                {
                    value: 'complicated',
                    emoji: '😔',
                    label: this.translations.complicated,
                },
                {
                    value: 'good',
                    emoji: '🙂',
                    label: this.translations.good,
                },
                {
                    value: 'perfect',
                    emoji: '😍',
                    label: this.translations.perfect,
                },
            ];
        },
        deviceType() {
            const hasTouch = navigator.maxTouchPoints > 0;
            const isSmallScreen = window.matchMedia('(max-width: 768px)').matches;
            const mobileUA = /Mobi|Android|iPhone/i.test(navigator.userAgent);

            return (isSmallScreen && hasTouch) || mobileUA ? 'mobile' : 'pc';
        },
        isMobile() {
            return this.deviceType === 'mobile';
        },
    },
    mounted() {
        if (this.isMobile) {
            this.tooltipShowTimeout = setTimeout(() => {
                const tooltipElement = this.$refs.teaserTooltip?.$el;
                if (tooltipElement) {
                    tooltipElement.dispatchEvent(new MouseEvent('mouseenter'));
                    this.tooltipHideTimeout = setTimeout(() => {
                        tooltipElement.dispatchEvent(new MouseEvent('mouseleave'));
                    }, 2000);
                }
            }, 800);
        }
    },
    beforeUnmount() {
        clearTimeout(this.tooltipShowTimeout);
        clearTimeout(this.tooltipHideTimeout);
    },
    methods: {
        selectReaction(reaction) {
            this.selectedReaction = reaction;
            this.$nextTick(() => {
                this.$refs.feedbackTextarea?.setFocus();
            });
        },
        submitFeedback() {
            const feedbackData = {
                reaction: this.selectedReaction,
                comment: this.comment || null,
                device: this.deviceType,
                payment_method: this.data.payment_success?.method || null,
                payment_type: this.data.payment_success?.type || null,
            };
            this.isSubmitted = true;

            this.$ajax.post('/quickpay/' + this.hash + '/feedback', {}, feedbackData);
        },
        close() {
            this.isClosed = true;
        },
        openFeedbackPanel() {
            this.showMobileTeaser = false;
            clearTimeout(this.tooltipShowTimeout);
        },
    },
};
</script>

<style lang="scss" scoped>
.quickpay-payment-feedback {
    max-width: 358px;
    position: fixed;
    bottom: var(--spacing-4);
    right: var(--spacing-4);
    background: var(--colors-common-background-default);
    border-radius: var(--spacing-3);
    box-shadow: var(--elevation-1);
    z-index: 1001;

    @media (max-width: 768px) {
        right: 10px;
    }

    &:has(&__header) {
        padding: var(--spacing-3) var(--spacing-4);
    }

    &__header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: var(--spacing-0);
        margin-bottom: var(--spacing-1);
    }
    &__title {
        margin: 0;
        color: var(--colors-common-text-subtle-default);
    }
    &__buttons {
        display: flex;
        gap: var(--spacing-1);
    }
    &__emoji-button {
        flex: 1;
        display: flex;
        align-items: center;
        gap: var(--spacing-1);
        padding: var(--spacing-1) var(--spacing-2);
        background: var(--colors-common-background-default);
        border: 1px solid var(--colors-common-border-lighter);
        border-radius: var(--spacing-1);
        cursor: pointer;
        color: var(--colors-common-text-standard-caption);
        text-decoration: none;
        transition: all 0.2s ease;
        &:hover,
        &.is-active {
            color: var(--colors-common-text-subtle-default);
            border-color: var(--colors-common-border-default-hover);
        }
    }
    &__emoji {
        font-size: 14px;
    }
    &__form {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-2);
        margin-top: var(--spacing-3);
    }
    &__button {
        width: 100%;
        padding: 6px;
        background: var(--colors-common-background-darkest-default);
        color: var(--colors-common-text-standard-on-color);
        border: none;
        border-radius: var(--spacing-0);
        cursor: pointer;
    }

    &__thanks {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-3);
    }
    &__description {
        color: var(--colors-common-text-standard-placeholder);
        margin: 0;
    }

    &__teaser-bubble {
        width: 72px;
        height: 56px;
        border-radius: var(--spacing-3);
        background: var(--colors-common-background-default);
        box-shadow: var(--elevation-1);
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
    }
}
</style>
